import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Users, Award } from 'lucide-react';
import axios from 'axios';
import CaseCard from './CaseCard';
import { useSchemaOrg } from '../hooks/useSchemaOrg';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const CustomerCases = ({ limit = 3, showHeader = true, showCTA = true }) => {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCases();
  }, [limit]);

  const fetchCases = async () => {
    try {
      const response = await axios.get(`${API}/cases/featured?limit=${limit}`);
      setCases(response.data);
    } catch (error) {
      console.error('Error fetching cases:', error);
    } finally {
      setLoading(false);
    }
  };

  const { name: companyName, SITE_URL } = useSchemaOrg();

  // Generate structured data - Review Schema wrapped in ItemList
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": `Kundesuksesshistorier fra ${companyName}`,
    "description": `Se hvordan våre kunder har oppnådd suksess med ${companyName} sine verkstedtjenester`,
    "numberOfItems": cases.length,
    "itemListElement": cases.map((caseItem, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Review",
        "itemReviewed": {
          "@type": "AutoRepair",
          "name": companyName,
          "@id": `${SITE_URL}/#organization`
        },
        "author": {
          "@type": "Organization",
          "name": caseItem.customer_name,
          "address": {
            "@type": "PostalAddress",
            "addressLocality": caseItem.location
          }
        },
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": caseItem.rating,
          "bestRating": "5",
          "worstRating": "1"
        },
        "reviewBody": caseItem.quote,
        "datePublished": caseItem.date_published,
        "name": caseItem.title
      }
    }))
  };

  if (loading) {
    return (
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center">
            <div className="text-gray-400">Laster kundehistorier...</div>
          </div>
        </div>
      </section>
    );
  }

  if (cases.length === 0) {
    return null;
  }

  return (
    <section 
      className="py-20 bg-gradient-to-b from-black via-gray-900 to-black"
      aria-label="Kundesuksesshistorier"
      id="customer-cases"
    >
      {/* SEO: Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
      
      <div className="container mx-auto px-4">
        {/* Header */}
        {showHeader && (
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-600/20 rounded-full border border-yellow-600/30 mb-6">
              <Award className="w-5 h-5 text-yellow-500" />
              <span className="text-sm text-yellow-400 font-medium">Dokumenterte resultater</span>
            </div>
            
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Kundesuksess
              <span className="bg-gradient-to-r from-red-500 to-yellow-400 bg-clip-text text-transparent">
                {' '}historier
              </span>
            </h2>
            
            <p className="text-gray-400 max-w-2xl mx-auto text-lg">
              Se hvordan våre kunder har oppnådd målbare resultater med Sand-tec som partner. 
              Fra redusert nedetid til bedre flåteutnyttelse.
            </p>
          </div>
        )}

        {/* Cases Grid - First-View Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {cases.map((caseItem) => (
            <CaseCard key={caseItem.id} caseData={caseItem} />
          ))}
        </div>

        {/* CTA */}
        {showCTA && (
          <div className="text-center mt-12">
            <Link
              to="/referanser"
              className="inline-flex items-center px-8 py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold rounded-lg transition-all group"
            >
              <Users className="w-5 h-5 mr-2" />
              Se alle referanser
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

export default CustomerCases;
